import styled from 'styled-components'

/**
 * AbwaabPlus mobile-first page shell + sticky bottom action bar.
 *
 * `Screen` gives every page a consistent edge-to-edge phone layout: horizontal
 * `--screen-pad-x` gutters, a bottom safe area (`--content-bottom-pad`) so fixed
 * bottom bars / nav never cover content, and an optional content cap. It widens
 * its gutters on larger screens. Reads the GlobalStyles token layer, so it
 * recolors/respaces with the active theme. Import from `@Components/AbwaabPlus`.
 *
 * `BottomActionBar` is the native-app "primary CTA pinned to the bottom" pattern
 * (buy a classroom/course, submit a test/assignment). It is `sticky` on phones
 * and dissolves into the normal flow (`static`) from `--abw-bar-bp` (default
 * 768px) up, where the desktop layout shows the action inline instead.
 */

/** Max content width on wide screens. Defaults to a comfy reading column. */
export const Screen = styled.div<{ $maxWidth?: number | string; $bottomBar?: boolean }>`
	width: 100%;
	max-width: ${p => (p.$maxWidth ? (typeof p.$maxWidth === 'number' ? `${p.$maxWidth}px` : p.$maxWidth) : '1180px')};
	margin: 0 auto;
	padding-inline: var(--screen-pad-x);
	padding-top: var(--screen-pad-y);
	/* leave room for the sticky action bar / bottom nav on phones */
	padding-bottom: ${p => (p.$bottomBar ? 'calc(var(--content-bottom-pad) + 56px)' : 'var(--content-bottom-pad)')};
	${({ theme }) => `${theme.mediaQueries.large}{
		padding-inline: 24px;
	}`}
`

export const BottomActionBar = styled.div`
	position: sticky;
	bottom: 0;
	z-index: 40;
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	padding: 12px var(--screen-pad-x);
	padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
	background: var(--surface-card);
	border-top: 1px solid var(--border);
	box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
	/* primary button fills the remaining width, 44px+ tap target */
	> button,
	> a {
		flex: 1;
		min-height: var(--touch-min);
	}
	${({ theme }) => `${theme.mediaQueries.medium}{
		display: none;
	}`}
`
